import {Hemisphere} from '../types';

// The Journal's date kicker: "Friday 17 October · mid-spring". Seasons are meteorological
// (three whole months each) and flip for the southern hemisphere; early/mid/late is the
// month's place within the season.

const SEASONS_NORTH = ['winter', 'winter', 'spring', 'spring', 'spring', 'summer', 'summer', 'summer', 'autumn', 'autumn', 'autumn', 'winter'];
const PART = ['mid', 'late', 'early', 'mid', 'late', 'early', 'mid', 'late', 'early', 'mid', 'late', 'early'];

export function seasonLabel(date: Date, hemisphere: Hemisphere | null | undefined): string {
    const month = date.getMonth();
    const index = hemisphere === 'S' ? (month + 6) % 12 : month;
    return `${PART[index]}-${SEASONS_NORTH[index]}`;
}

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;

/** The whole meteorological season containing `date`, as local "YYYY-MM-DD" bounds. */
export function seasonRange(date: Date, hemisphere: Hemisphere | null | undefined): {from: string; to: string; name: string} {
    const name = seasonLabel(date, hemisphere).split('-')[1];
    // Walk back to the first month of this season (at most two steps).
    let y = date.getFullYear();
    let m = date.getMonth();
    for (let i = 0; i < 2; i++) {
        const prev = new Date(y, m - 1, 1);
        if (seasonLabel(prev, hemisphere).split('-')[1] !== name) break;
        y = prev.getFullYear();
        m = prev.getMonth();
    }
    const end = new Date(y, m + 3, 0); // last day of the third month
    return {from: ymd(y, m, 1), to: ymd(end.getFullYear(), end.getMonth(), end.getDate()), name};
}

export const yearRange = (date: Date): {from: string; to: string} => ({from: `${date.getFullYear()}-01-01`, to: `${date.getFullYear()}-12-31`});

const kickerFormat = new Intl.DateTimeFormat('en-NZ', {weekday: 'long', day: 'numeric', month: 'long'});

export const dateKicker = (date: Date, hemisphere: Hemisphere | null | undefined): string =>
    `${kickerFormat.format(date)} · ${seasonLabel(date, hemisphere)}`;

// ---------------------------------------------------------------- growing seasons (Phase 2)
// A plant's story is told one season at a time. In the south a season runs 1 June to 31 May and
// is labelled "2025/26" (June garlic stays with its December harvest); in the north it is the
// calendar year, "2025". The same maths lives in src/posts/seasons.ts.

const SOUTH_START_MONTH = 6;

/** The season key for a local date or whenLocal: "2025/26" in the south, "2025" in the north. */
export function seasonKey(whenLocal: string, hemisphere: Hemisphere | null | undefined): string {
    const year = Number(whenLocal.slice(0, 4));
    if (hemisphere !== 'S') return String(year);
    const month = Number(whenLocal.slice(5, 7));
    const start = month >= SOUTH_START_MONTH ? year : year - 1;
    return `${start}/${String(start + 1).slice(2)}`;
}

/** The local dates a season key covers, inclusive. */
export function seasonBounds(key: string, hemisphere: Hemisphere | null | undefined): {from: string; to: string} {
    const start = Number(key.slice(0, 4));
    if (hemisphere !== 'S') return {from: `${start}-01-01`, to: `${start}-12-31`};
    return {from: `${start}-${pad(SOUTH_START_MONTH)}-01`, to: `${start + 1}-${pad(SOUTH_START_MONTH - 1)}-31`};
}

export const currentSeasonKey = (today: string, hemisphere: Hemisphere | null | undefined): string => seasonKey(today, hemisphere);
